import { z } from 'zod';
import {
  AccountStatus,
  AppRole,
  Basis,
  Designation,
  DesignationStatus,
  EngagementAreaKey,
  PromoCatalogue,
  SpecStatus,
  Specialization,
  WorkloadCategory,
} from '@exigo/shared';

/**
 * The `/reference` response shape, mirroring `apps/api/src/routes/reference.ts`.
 *
 * Deliberately thin: no tier payouts, no sub-floors, no capability terms. Those
 * are the Microsoft-confidential part of the guide and the API never sends them —
 * see security.md §14 (A04) and the test asserting exactly that.
 */
export const ReferenceData = z
  .object({
    guideVersion: z.string(),
    guideLabel: z.string(),
    guideTerm: z.string(),
    rulesetHash: z.string(),
    areas: z.array(z.object({ key: z.string(), name: z.string(), sub: z.string() })),
    basesByArea: z.record(EngagementAreaKey, z.array(Basis)),
    segments: z.array(z.string()),
    specializations: z.array(Specialization),
    designations: z.array(Designation),
    workloadCategories: z.array(WorkloadCategory),
    familyLabels: z.record(z.string(), z.string()),
    defaults: z.object({
      specs: z.record(z.string(), SpecStatus),
      designations: z.record(z.string(), DesignationStatus),
      workloads: z.array(z.object({ name: z.string(), category: z.string() })),
    }),
    /**
     * The CSP promotion catalogue. Note what is *not* here: the Business
     * Processes bands that price the band-drop risk. Those are MCI payouts and
     * stay server-side, so the band verdict arrives from /promotions/evaluate
     * already computed.
     */
    promoCatalogue: PromoCatalogue,
  })
  .strict();
export type ReferenceData = z.infer<typeof ReferenceData>;

/** GET /auth/session's response — the client's only source of identity. */
export const SessionUser = z
  .object({
    id: z.number(),
    name: z.string(),
    email: z.string(),
    initials: z.string(),
    azureId: z.string().nullable(),
    role: AppRole,
    /**
     * `pending` (or `rejected`/`disabled`) means authentication succeeded but
     * an admin has not granted access. This is the only route such an account
     * can reach, which is why the status arrives here — see App.tsx's Gate.
     */
    status: AccountStatus,
  })
  .strict();
export type SessionUser = z.infer<typeof SessionUser>;

export const SessionResponse = z
  .object({
    user: SessionUser,
    csrfToken: z.string(),
  })
  .strict();
export type SessionResponse = z.infer<typeof SessionResponse>;

/** POST /promotions/import — a freshly parsed Partner Center export. */
export const PromoImportResponse = z
  .object({
    catalogue: PromoCatalogue,
    /** Rows read before grouping, so the UI can say how many SKUs collapsed. */
    skuRowsRead: z.number().int().min(0),
  })
  .strict();
export type PromoImportResponse = z.infer<typeof PromoImportResponse>;

export const VersionResponse = z
  .object({
    engineVersion: z.string(),
    guideVersion: z.string(),
    guideLabel: z.string(),
    rulesetHash: z.string(),
  })
  .strict();
export type VersionResponse = z.infer<typeof VersionResponse>;

/** RFC 9457 problem details — matches apps/api/src/errors.ts. */
export const ProblemDetails = z
  .object({
    type: z.string(),
    title: z.string(),
    status: z.number(),
    detail: z.string(),
    instance: z.string().optional(),
    correlationId: z.string().optional(),
  })
  .partial({ type: true });
export type ProblemDetails = z.infer<typeof ProblemDetails>;
